"use client";

import { type InputHTMLAttributes, type Ref } from "react";

import { cn } from "../../utils";
import styles from "./Checkbox.module.scss";

export type CheckboxSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "size"
> {
  ref?: Ref<HTMLInputElement>;
  size?: CheckboxSize;
  value: boolean;
  label?: string;
  labelClassName?: string;
  onValueChange?: (value: boolean) => void;
}

export const Checkbox = ({
  size = "sm",
  value,
  label,
  disabled = false,
  className,
  labelClassName,
  onChange,
  onValueChange,
  ref,
  ...props
}: CheckboxProps) => {
  return (
    <label
      className={cn(
        "ui:flex ui:cursor-pointer ui:items-center ui:gap-2 ui:text-textSecondary ui:select-none",
        labelClassName,
      )}
    >
      <input
        ref={ref}
        checked={value}
        type="checkbox"
        disabled={disabled}
        className={cn(
          styles.CheckboxInput,
          "ui:relative ui:inline-block ui:shrink-0 ui:cursor-pointer ui:appearance-none ui:rounded-[5px] ui:border ui:border-divider ui:bg-transparent ui:text-white ui:shadow-none ui:transition-[border-color,background-color] ui:duration-200 ui:checked:border-primary ui:checked:bg-primary ui:focus-visible:outline-2 ui:focus-visible:outline-offset-2 ui:focus-visible:outline-primary ui:enabled:hover:border-primary ui:disabled:cursor-not-allowed ui:disabled:opacity-20 ui:dark:text-black",
          {
            "ui:size-4 ui:p-0.5": size === "xs",
            "ui:size-5 ui:p-0.75": size === "sm",
            "ui:size-6 ui:p-1": size === "md",
            "ui:size-7 ui:p-1.25": size === "lg",
            "ui:size-8 ui:p-1.5": size === "xl",
          },
          className,
        )}
        onChange={(e) => {
          onChange?.(e);
          onValueChange?.(e.target.checked);
        }}
        {...props}
      />
      {label}
    </label>
  );
};
